import {
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
  sub
} from '@sequencehq/utils/dates'

export type PushablePeriod = {
  /** yyyy-MM, also used as the row key/test id. */
  id: string
  label: string
  /** Inclusive start of the period, yyyy-MM-dd. */
  start: string
  /** Inclusive end of the period, yyyy-MM-dd. */
  end: string
}

// How far back the pushable window reaches — a fixed trailing window of full,
// elapsed months.
const WINDOW_MONTHS = 12

/**
 * The pushable periods for the Push to Xero modal. The months come from the Xero
 * sync-status endpoint (one entry per calendar month with recognition journals),
 * so no separate journals fetch is needed. Offers every such month in the
 * trailing {@link WINDOW_MONTHS}-month window up to the end of last month — the
 * current month is a half-month, not pushable.
 */
export const derivePushablePeriods = (
  monthKeys: Iterable<string>,
  now: Date
): PushablePeriod[] => {
  const currentMonth = startOfMonth(now)
  const from = format(
    startOfMonth(sub(currentMonth, { months: WINDOW_MONTHS })),
    'yyyy-MM-dd'
  )
  const to = format(endOfMonth(sub(currentMonth, { months: 1 })), 'yyyy-MM-dd')

  return [...monthKeys]
    .map(monthBounds)
    .filter(period => period.start >= from && period.end <= to)
    .sort((a, b) => b.start.localeCompare(a.start))
}

// yyyy-MM -> the month's bounds. parseISO runs in UTC (see the utils date
// helpers), so the month boundary is stable regardless of the viewer's zone.
const monthBounds = (yyyyMm: string): PushablePeriod => {
  const monthStart = parseISO(`${yyyyMm}-01`)
  return {
    id: yyyyMm,
    label: format(monthStart, 'MMMM yyyy'),
    start: format(monthStart, 'yyyy-MM-dd'),
    end: format(endOfMonth(monthStart), 'yyyy-MM-dd')
  }
}
